import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import axiosInstance from "@/utils/appsettings";

// Figma redirects the browser here after the user approves access. This page exists because the
// redirect is a GET the browser follows, while the server's exchange endpoint is a POST — without
// something to bridge the two, the redirect lands on a route that does not exist and the user sees
// a 404 with their connection half-made.
//
// It runs in the popup opened by the Connectors screen, so it also has to tell the opener it
// finished; otherwise the Connectors screen keeps showing "Not connected" until a manual reload.

export const FIGMA_CONNECTED_MESSAGE = "joylo:figma-connected";

const FigmaCallback: React.FC = () => {
  const [status, setStatus] = useState<"working" | "done" | "failed">("working");
  const [detail, setDetail] = useState("Finishing the connection…");
  // React 18 StrictMode mounts effects twice in dev, and the state token is single-use — a second
  // exchange would fail and report a working connection as broken
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current) return;
    exchanged.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const denied = params.get("error");

    if (denied) {
      setStatus("failed");
      setDetail(
        denied === "access_denied"
          ? "You declined access, so nothing was connected."
          : `Figma reported: ${denied}`,
      );
      return;
    }

    if (!code || !state) {
      setStatus("failed");
      setDetail("That link is missing its authorization code. Start the connection again from Joylo.");
      return;
    }

    void (async () => {
      try {
        // Deliberately unauthenticated on the server: the single-use state token identifies the
        // user, so this works even though a popup may not carry a session
        await axiosInstance.post("/figma/auth/callback", { code, state });
        setStatus("done");
        setDetail("Figma is connected.");
        try {
          window.opener?.postMessage({ type: FIGMA_CONNECTED_MESSAGE }, window.location.origin);
        } catch {
          // An opener from another origin cannot be messaged; the window still closes below
        }
        setTimeout(() => window.close(), 1200);
      } catch (err) {
        const message = (err as { response?: { data?: { detail?: { error?: { message?: string } } } } })
          ?.response?.data?.detail?.error?.message;
        setStatus("failed");
        setDetail(message || "Could not complete the Figma connection. Please try again from Joylo.");
      }
    })();
  }, []);

  const Icon = status === "working" ? Loader2 : status === "done" ? CheckCircle2 : XCircle;
  const tone =
    status === "working" ? "text-muted-foreground" : status === "done" ? "text-emerald-600" : "text-destructive";

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6">
      <div className="w-full max-w-sm rounded-[14px] border border-line bg-surface p-6 text-center">
        <Icon className={`mx-auto h-8 w-8 ${tone} ${status === "working" ? "animate-spin" : ""}`} />
        <h1 className="mt-4 text-base font-semibold text-foreground">
          {status === "done" ? "Connected to Figma" : status === "failed" ? "Connection failed" : "Connecting Figma"}
        </h1>
        <p className="mt-1.5 text-sm text-muted-foreground">{detail}</p>
        {status === "done" && (
          <p className="mt-3 text-xs text-muted-foreground">This window closes on its own.</p>
        )}
        {status === "failed" && (
          <button
            type="button"
            onClick={() => window.close()}
            className="mt-4 text-xs text-muted-foreground underline"
          >
            Close this window
          </button>
        )}
      </div>
    </div>
  );
};

export default FigmaCallback;
